import React from 'react';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import Styled from 'styled-components/macro';
import {ClientContactNode} from './CompanyViewPage';
import {Breadcrumbs, Link} from '@forecast-it/design-system';
import {CSS_CONSTANTS} from '../../css_variables';
import {hasPermission} from '../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import {hasFeatureFlag} from '../shared/util/FeatureUtil';
import {FEATURE_FLAGS} from '../../constants';
import Util from '../shared/util/util';
import {MODAL_TYPE, showModal} from '../shared/components/modals/generic_modal_conductor';
import DeleteClientMutation from '../../mutations/delete_client_mutation';
import {createToast} from '../shared/components/toasts/another-toast/toaster';
import DropdownMenu from '../shared/components/dropdown-menu/DropdownMenu';

type ClientStatus = {
	standing: 'ACTIVE' | 'INACTIVE';
};

type PrimaryContact = {
	id: string;
	firstName: string | null;
	lastName: string | null;
};

type Client = {
	id: string;
	name: string;
	website: string | null;
	city: string | null;
	country: string | null;
	region: string | null;
	statusId: string | null;
	createdAt: string | null;
	updatedAt: string | null;
};

type Props = {
	client: Client;
	status: ClientStatus | null;
	primaryContact: PrimaryContact | null;
	clientContacts: ClientContactNode[];
	companyId: string;
};

// sky/sys/color/text/content/main
const COLOR_TEXT_MAIN = '#232334';
// sky/sys/color/text/content/auxiliary
const COLOR_TEXT_AUXILIARY = '#6b6d7b';
// sky/sys/color/brand/primary/1
const COLOR_BRAND_PRIMARY = '#0004db';
// sky/sys/color/id/9 (active/on-track green)
const COLOR_STATUS_ACTIVE = '#27bb8f';
// sky/sys/color/text/content/ondark
const COLOR_TEXT_ON_DARK = '#ffffff';

const Header = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const PageHeader = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 8px 32px 16px 32px;
`;

const TitleRow = Styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const ClientName = Styled.h1`
	font-size: 24px;
	font-weight: 500;
	line-height: 32px;
	color: ${COLOR_TEXT_MAIN};
	margin: 0;
`;

const TitleActions = Styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const StatusBadge = Styled.span<{standing: 'ACTIVE' | 'INACTIVE'}>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 8px 16px;
	border: 2px solid ${({standing}) => (standing === 'ACTIVE' ? COLOR_STATUS_ACTIVE : CSS_CONSTANTS.v2_text_light_gray)};
	border-radius: 8px;
	font-size: 16px;
	line-height: 24px;
	letter-spacing: 0.15px;
	color: ${COLOR_TEXT_MAIN};
	background-color: #ffffff;
`;

const StatusDot = Styled.span<{standing: 'ACTIVE' | 'INACTIVE'}>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: ${({standing}) => (standing === 'ACTIVE' ? COLOR_STATUS_ACTIVE : CSS_CONSTANTS.v2_text_light_gray)};
`;

const Infobar = Styled.div`
	display: flex;
	align-items: center;
	gap: 38px;
	padding-left: 32px;
	font-size: 16px;
	line-height: 24px;
	letter-spacing: 0.15px;
`;

const DetailItem = Styled.span`
	display: flex;
	align-items: center;
	gap: 9px;
`;

const DetailLabel = Styled.span`
	color: ${COLOR_TEXT_AUXILIARY};
`;

const DetailValue = Styled.span`
	font-weight: 500;
	color: ${COLOR_TEXT_MAIN};
`;

const ContactAvatar = Styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	border-radius: 4px;
	background-color: ${COLOR_BRAND_PRIMARY};
	font-size: 12px;
	font-weight: 700;
	line-height: 16px;
	letter-spacing: 0.3px;
	text-transform: uppercase;
	color: ${COLOR_TEXT_ON_DARK};
	flex-shrink: 0;
`;

const CompanyViewHeader: React.FC<Props> = ({client, status, primaryContact, companyId}) => {
	const intl = useIntl();
	const history = useHistory();
	const primaryContactInitials = primaryContact
		? `${primaryContact.firstName?.[0] ?? ''}${primaryContact.lastName?.[0] ?? ''}`.toUpperCase()
		: null;
	const primaryContactName = primaryContact
		? [primaryContact.firstName, primaryContact.lastName].filter(Boolean).join(' ')
		: null;

	const locationParts = [client.city, client.region, client.country].filter(Boolean);
	const location = locationParts.length > 0 ? locationParts.join(', ') : null;

	const none = intl.formatMessage({id: 'company_view_page.field_none'});
	const clientsListPath = hasFeatureFlag(FEATURE_FLAGS.NEW_CLIENTS_FEATURE) ? '/clients' : '/admin/clients';

	const formatDate = (iso: string | null): string => {
		if (!iso) return none;
		return intl.formatDate(new Date(iso), {month: 'short', day: 'numeric', year: 'numeric'});
	};

	const handleDelete = () => {
		const onSuccess = () => {
			createToast({
				duration: 5000,
				message: intl.formatMessage({id: 'company_view_page.client_deleted_toast'}),
			});
			history.push(clientsListPath);
		};
		showModal({
			type: MODAL_TYPE.GENERIC_DELETION_WARNING,
			deleteCallback: () => {
				Util.CommitMutation(DeleteClientMutation, {id: client.id, companyId}, onSuccess);
			},
		});
	};

	return (
		<Header>
			<PageHeader>
				<Breadcrumbs>
					<Breadcrumbs.Item>
						<Link href={clientsListPath}>
							{intl.formatMessage({id: 'company_view_page.breadcrumb_all_clients'})}
						</Link>
					</Breadcrumbs.Item>
					<Breadcrumbs.Item>
						<span>{client.name}</span>
					</Breadcrumbs.Item>
				</Breadcrumbs>

				<TitleRow>
					<ClientName>{client.name}</ClientName>
					<TitleActions>
						{status && (
							<StatusBadge standing={status.standing}>
								<StatusDot standing={status.standing} />
								{intl.formatMessage({
									id:
										status.standing === 'ACTIVE'
											? 'company_view_page.status_active'
											: 'company_view_page.status_inactive',
								})}
							</StatusBadge>
						)}
						<DropdownMenu>
							<DropdownMenu.Item
								onClick={() => showModal({type: MODAL_TYPE.EDIT_CLIENT_DETAILS, clientId: client.id})}
							>
								{intl.formatMessage({id: 'company_view_page.edit_client'})}
							</DropdownMenu.Item>
							{hasPermission(PERMISSION_TYPE.CLIENT_UPDATE) && (
								<DropdownMenu.Item onClick={handleDelete}>
									{intl.formatMessage({id: 'company_view_page.delete_client'})}
								</DropdownMenu.Item>
							)}
						</DropdownMenu>
					</TitleActions>
				</TitleRow>
			</PageHeader>

			<Infobar>
				<DetailItem>
					<DetailLabel>{intl.formatMessage({id: 'company_view_page.website_label'})}</DetailLabel>
					<DetailValue>{client.website ?? none}</DetailValue>
				</DetailItem>
				<DetailItem>
					<DetailLabel>{intl.formatMessage({id: 'company_view_page.primary_contact_label'})}</DetailLabel>
					{primaryContact ? (
						<>
							<ContactAvatar>{primaryContactInitials}</ContactAvatar>
							<DetailValue>{primaryContactName}</DetailValue>
						</>
					) : (
						<DetailValue>{none}</DetailValue>
					)}
				</DetailItem>
				<DetailItem>
					<DetailLabel>{intl.formatMessage({id: 'company_view_page.client_location_label'})}</DetailLabel>
					<DetailValue>{location ?? none}</DetailValue>
				</DetailItem>
				<DetailItem>
					<DetailLabel>{intl.formatMessage({id: 'company_view_page.created_date_label'})}</DetailLabel>
					<DetailValue>{formatDate(client.createdAt)}</DetailValue>
				</DetailItem>
				<DetailItem>
					<DetailLabel>{intl.formatMessage({id: 'company_view_page.modified_date_label'})}</DetailLabel>
					<DetailValue>{formatDate(client.updatedAt)}</DetailValue>
				</DetailItem>
			</Infobar>
		</Header>
	);
};

export default CompanyViewHeader;
